import { useState, useEffect } from 'react';

import AngleBracketsIcon from '@atlaskit/icon/core/angle-brackets';
import AppIcon from '@atlaskit/icon/core/app';
import AppsIcon from '@atlaskit/icon/core/apps';
import BookWithBookmarkIcon from '@atlaskit/icon/core/book-with-bookmark';
import BranchIcon from '@atlaskit/icon/core/branch';
import CloudArrowUpIcon from '@atlaskit/icon/core/cloud-arrow-up';
import CommitIcon from '@atlaskit/icon/core/commit';
import DownloadIcon from '@atlaskit/icon/core/download';
import FlaskIcon from '@atlaskit/icon/core/flask';
import FolderOpenIcon from '@atlaskit/icon/core/folder-open';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import PagesIcon from '@atlaskit/icon/core/pages';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import PersonAvatarIcon from '@atlaskit/icon/core/person-avatar';
import PullRequestIcon from '@atlaskit/icon/core/pull-request';
import SettingsIcon from '@atlaskit/icon/core/settings';
import SnippetIcon from '@atlaskit/icon/core/snippet';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import JiraIcon from '@atlaskit/icon-lab/core/jira';
import MergeQueueIcon from '@atlaskit/icon-lab/core/merge-queue';
import PipelineIcon from '@atlaskit/icon-lab/core/pipeline';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import TakeoutContainerIcon from '@atlaskit/icon-lab/core/takeout-container';
import { ContainerAvatar } from '@atlaskit/navigation-system/side-nav-items/container-avatar';

import { ShieldIcon } from 'src/components/icon';
import {
  NewLozenge,
  BetaLozenge,
  PremiumLozenge,
} from 'src/components/lozenges';
import { MenuItem } from 'src/components/navigation/src/types';

// returns content to render at the end of a menu item (controlled by API)
// matches what's currently done in container navigation v3
export function getLinkItemAfterContent(menuItem: MenuItem) {
  if (menuItem.is_beta) {
    return <BetaLozenge />;
  } else if (menuItem.is_new) {
    return <NewLozenge />;
  } else if (menuItem.is_premium) {
    return <PremiumLozenge />;
  } else if (menuItem.target === '_blank') {
    return <LinkExternalIcon label="" />;
  }
  return null;
}

// handles rendering a fallback icon for any forge/connect apps
// that pass broken icon urls. otherwise renders default app icon
const ThirdPartyAppIcon = ({ iconUrl }: { iconUrl?: string }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (iconUrl) {
      const img = new Image();
      img.onerror = () => setHasError(true);
      img.src = iconUrl;
    }
  }, [iconUrl]);

  // if no custom icon is passed for forge/connect app or the url is broken,
  // just render default App icon
  if (!iconUrl || hasError) {
    return <AppIcon label="" />;
  }

  return <ContainerAvatar src={iconUrl} />;
};

// This is similar to the icon map from
// `./src/components/navigation/src/components/icons-map.tsx` but specific to
// Nav4.
export const getIconForMenuItem = (menuItem: MenuItem) => {
  switch (menuItem.tab_name) {
    // Global menu items
    case 'overview':
      return <PersonAvatarIcon label="" />;
    case 'repositories':
      return <BookWithBookmarkIcon label="" />;
    case 'pullrequests':
      return <PullRequestIcon label="" />;
    case 'projects':
      return <FolderOpenIcon label="" />;
    case 'people':
      return <PeopleGroupIcon label="" />;
    case 'packages':
      return <TakeoutContainerIcon label="" />;
    case 'snippets':
      return <SnippetIcon label="" />;

    // Repository menu items
    case 'source':
      return <AngleBracketsIcon label="" />;
    case 'commits':
      return <CommitIcon label="" />;
    case 'branches':
      return <BranchIcon label="" />;
    case 'pipelines':
      return <PipelineIcon label="" />;
    case 'deployments':
      return <CloudArrowUpIcon label="" />;
    case 'issues':
      return <WorkItemsIcon label="" />;
    case 'jira':
      return <JiraIcon label="" />;
    case 'security':
      return <ShieldIcon label="" />;
    case 'wiki':
      return <PagesIcon label="" />;
    case 'downloads':
      return <DownloadIcon label="" />;
    case 'admin':
      return <SettingsIcon label="" />;
    case 'testmanagement':
      return <FlaskIcon label="" />;
    case 'mergequeues':
      return <MergeQueueIcon label="" />;
    case 'agents':
      return <RovoIcon label="" />;
    default:
      break;
  }

  if (
    menuItem.type === 'forge_menu_item' ||
    menuItem.type === 'connect_menu_item'
  ) {
    return <ThirdPartyAppIcon iconUrl={menuItem.icon_url} />;
  }

  // Default icon
  return <AppsIcon label="" />;
};
